import { FireWallExpressionCondition, WafAction } from "../../../../utils/constants";

const CONTAINS_FILTER_KEYS = new Set([
  "uri",
  "hostname",
  "host_name",
  "host",
  "ua",
  "user_agent",
  "operator",
  "block_reason",
  "attack_type",
  "bot_name",
  "bot_type",
  "type",
  "referer",
  "query_string",
]);

const FIELD_MAPS = {
  firewall: {
    id: "request_id",
    request_id: "request_id",
    src_ip: "src_ip",
    hostname: "hostname",
    host_name: "hostname",
    host: "hostname",
    uri: "uri",
    country: "country",
    method: "method",
    resStatus: "res_code",
    res_code: "res_code",
    ua: "ua",
    user_agent: "ua",
    rule_id: "rule_id",
    block_reason: "reason",
    operator: "operator",
  },
  bot: {
    id: "request_id",
    request_id: "request_id",
    src_ip: "src_ip",
    source_ip: "src_ip",
    hostname: "hostname",
    host_name: "hostname",
    host: "hostname",
    uri: "uri",
    country: "country",
    method: "method",
    resStatus: "res_code",
    res_code: "res_code",
    bot_type: "bot_type",
    bot_name: "bot_name",
    // Top user agent card / drill-down (bot_type | ja4 | http_version label)
    ua: "ua",
    user_agent: "ua",
    ja3_hash: "ja3_hash",
    threshold: "threshold",
  },
  ratelimit: {
    id: "request_id",
    request_id: "request_id",
    src_ip: "src_ip",
    source_ip: "src_ip",
    hostname: "hostname",
    host_name: "hostname",
    host: "hostname",
    uri: "uri",
    country: "country",
    method: "method",
    resStatus: "res_code",
    res_code: "res_code",
    ja3_hash: "ja3_hash",
    action: "action",
  },
  waf: {
    id: "request_id",
    request_id: "request_id",
    hostname: "hostname",
    host: "hostname",
    ip: "src_ip",
    uri: "uri",
    src_ip: "src_ip",
    country: "country",
    attack_type: "attack_type",
    risk_level: "risk_level",
    verdict: "verdict",
    method: "method",
    ua: "ua",
    user_agent: "ua",
    ja3_hash: "ja3_hash",
    res_code: "res_code",
    resStatus: "res_code",
    severity: "severity",
    source: "source",
    waf_type: "source",
    category: "category",
    asn: "asn",
    isp: "isp",
    referer: "referer",
    query_string: "query_string",
    type: "type",
    action: "action",
  },
};

function normalizeRateLimitActionValue(valueStr) {
  const v = String(valueStr).trim().toLowerCase();
  if (v === "blocked" || v === "block") return "block";
  if (v === "challenge" || v === "challenged") return "challenge";
  if (v === "log only" || v === "log_only" || v === "log") return "log_only";
  return String(valueStr).trim();
}

function normalizeFilterValue(filterId, apiFieldKey, valueStr, eventType) {
  let processedValue = valueStr;
  if (apiFieldKey === "res_code" || filterId === "resStatus") {
    const numValue = parseInt(processedValue, 10);
    if (!isNaN(numValue)) {
      processedValue = String(numValue);
    }
  }
  if (apiFieldKey === "hostname" && typeof processedValue === "string") {
    processedValue = processedValue.toLowerCase();
  }
  if (eventType === "ratelimit" && (filterId === "status" || filterId === "action" || apiFieldKey === "action")) {
    processedValue = normalizeRateLimitActionValue(processedValue);
  }
  return processedValue;
}

function getConditionType(filterId, apiFieldKey) {
  if (CONTAINS_FILTER_KEYS.has(filterId) || CONTAINS_FILTER_KEYS.has(apiFieldKey)) {
    return FireWallExpressionCondition.CONTAINS;
  }
  return FireWallExpressionCondition.EQUALS;
}

/**
 * Build ClickHouse log API conditions: [{ key, value, condition }, ...]
 * Matches backend buildConditionFilters (clickhouse.js).
 */
export function buildConditionsFromFilters(filters, eventType) {
  const fieldMap = FIELD_MAPS[eventType] || {};
  const out = [];

  filters
    .filter((f) => f.value && String(f.value).trim() !== "")
    .forEach((f) => {
      if (f.id === "status") {
        if (eventType === "ratelimit") {
          const valueParts = String(f.value)
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
          valueParts.forEach((valueStr) => {
            out.push({
              key: "action",
              value: normalizeRateLimitActionValue(valueStr),
              condition: FireWallExpressionCondition.EQUALS,
            });
          });
        }
        return;
      }
      if (f.id === "action") {
        if (eventType === "ratelimit") {
          const valueParts = String(f.value)
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
          valueParts.forEach((valueStr) => {
            out.push({
              key: "action",
              value: normalizeRateLimitActionValue(valueStr),
              condition: FireWallExpressionCondition.EQUALS,
            });
          });
        }
        return;
      }

      const apiFieldKey = fieldMap[f.id] || f.id;
      const valueParts = String(f.value)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);

      valueParts.forEach((valueStr) => {
        const processedValue = normalizeFilterValue(f.id, apiFieldKey, valueStr, eventType);
        out.push({
          key: apiFieldKey,
          value: processedValue,
          condition: getConditionType(f.id, apiFieldKey),
        });
      });
    });

  return out;
}

/** Append free-text `q` condition for detection event list/heatmap APIs. */
export function withSearchCondition(conditions, query) {
  const q = String(query || "").trim();
  if (!q) return conditions || [];
  return [
    ...(conditions || []),
    { key: "q", value: q, condition: FireWallExpressionCondition.CONTAINS },
  ];
}

export function getActionFromFilters(filters) {
  const statusFilter = filters.find(
    (f) => (f.id === "status" || f.id === "action") && f.value
  );
  if (!statusFilter) return WafAction.ALL;

  const statusValue = String(statusFilter.value).trim().toLowerCase();
  if (statusValue === "blocked" || statusValue === "block") return WafAction.BLOCK;
  if (statusValue === "challenged" || statusValue === "challenge") return WafAction.CHALLENGE;
  if (
    statusValue === "passed"
    || statusValue === "detect"
    || statusValue === "log only"
    || statusValue === "log_only"
  ) {
    return WafAction.DETECT;
  }
  return WafAction.ALL;
}
